import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

import { normalizeCommerceModel, type CommerceModel } from "@/lib/commerce-model";

export interface StoreItem {
  id: string;
  store_name: string;
  platform_type: string;
  country: string | null;
  base_currency: string | null;
  status: string;
  workspace_id: string | null;
  store_url: string | null;
  commerce_model: CommerceModel;
  platform_label: string | null;
  platform_icon: string | null;
}

interface StoreContextType {
  stores: StoreItem[];
  activeStoreId: string | null;
  activeStore: StoreItem | null;
  setActiveStoreId: (id: string | null) => void;
  isAllStores: boolean;
  isLoading: boolean;
  refetch: () => Promise<unknown>;
}

const StoreContext = createContext<StoreContextType>({
  stores: [],
  activeStoreId: null,
  activeStore: null,
  setActiveStoreId: () => {},
  isAllStores: true,
  isLoading: true,
  refetch: async () => undefined,
});

// sessionStorage key used by the impersonation platform picker to pin
// the active store to a specific commerce_model after impersonation starts.
const IMPERSONATE_PENDING_MODEL_KEY = "vazoul:impersonate:pending_model";

export function StoreProvider({ children }: { children: ReactNode }) {
  const { user, impersonation } = useAuth();
  const [activeStoreId, setActiveStoreId] = useState<string | null>(null);

  const { data: stores = [], isLoading, refetch } = useQuery({
    queryKey: ["user-stores"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("stores")
        .select("id, store_name, platform_type, base_currency, status, store_url, commerce_model, platform_label, platform_icon")
        .neq("platform_type", "cod_network")
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data || []).map((s: any) => ({
        ...s,
        country: s.country || null,
        workspace_id: s.workspace_id || null,
        commerce_model: normalizeCommerceModel(s.commerce_model),
        platform_label: s.platform_label ?? null,
        platform_icon: s.platform_icon ?? null,
      })) as StoreItem[];
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Impersonation platform picker: when operator selects a target platform
  // before starting impersonation, the desired commerce_model is stashed in
  // sessionStorage. Once impersonation-scoped stores load, pin the active
  // store to the first one matching that model.
  useEffect(() => {
    if (!impersonation?.active || stores.length === 0) return;
    let pending: string | null = null;
    try {
      pending = window.sessionStorage.getItem(IMPERSONATE_PENDING_MODEL_KEY);
    } catch { /* ignore */ }
    if (!pending) return;
    const match = stores.find((s) => s.commerce_model === pending);
    if (match) setActiveStoreId(match.id);
    try { window.sessionStorage.removeItem(IMPERSONATE_PENDING_MODEL_KEY); } catch { /* ignore */ }
  }, [impersonation?.active, stores]);

  const activeStore = stores.find((s) => s.id === activeStoreId) || null;
  const isAllStores = activeStoreId === null;

  return (
    <StoreContext.Provider value={{ stores, activeStoreId, activeStore, setActiveStoreId, isAllStores, isLoading, refetch }}>
      {children}
    </StoreContext.Provider>
  );
}

export function useStoreContext() {
  return useContext(StoreContext);
}

export { IMPERSONATE_PENDING_MODEL_KEY };
